import { z } from "zod";

/** What a caller sending no `assetTypes` gets, and the safe list to send. */
export const defaultAssetTypes = [
  "zap",
  "table",
  "interface",
  "canvas",
  "agent",
  "chatbot",
  "folder",
] as const;

/**
 * Every type the endpoint can return. Derived, so widening it cannot put a
 * type in front of callers that never asked for one.
 */
export const searchableAssetTypes = [
  ...defaultAssetTypes,
  "nextGenZap",
] as const;

export type SearchableAssetType = (typeof searchableAssetTypes)[number];

export const globalSearchInput = z.object({
  search: z.string().min(1, "Search term is required"),
  limitPerType: z.number().min(1).max(50).optional().default(5),
  assetTypes: z
    .array(z.enum(searchableAssetTypes))
    .optional()
    .default([...defaultAssetTypes]),
});

export type GlobalSearchInput = z.infer<typeof globalSearchInput>;

const baseSearchResultItem = z.object({
  id: z.string(),
  title: z.string(),
  updatedAt: z.string().optional(),
  createdAt: z.string().optional(),
  ownerId: z.union([z.string(), z.number()]).optional(),
  ownerName: z.string().optional(),
  ownerPhotoUrl: z.string().optional(),
  folderName: z.string().optional(),
});

const zapSearchResult = baseSearchResultItem.extend({
  assetType: z.literal("zap"),
  status: z.string().optional(),
  isEnabled: z.boolean().optional(),
});

const nextGenZapSearchResult = baseSearchResultItem.extend({
  assetType: z.literal("nextGenZap"),
  enabled: z.boolean().optional(),
  editorUrl: z.string().optional(),
});

const tableSearchResult = baseSearchResultItem.extend({
  assetType: z.literal("table"),
  kind: z.string().optional(),
});

const interfaceSearchResult = baseSearchResultItem.extend({
  assetType: z.literal("interface"),
  isPublished: z.boolean().optional(),
});

const canvasSearchResult = baseSearchResultItem.extend({
  assetType: z.literal("canvas"),
});

const agentSearchResult = baseSearchResultItem.extend({
  assetType: z.literal("agent"),
  enabled: z.boolean().optional(),
});

const chatbotSearchResult = baseSearchResultItem.extend({
  assetType: z.literal("chatbot"),
});

const folderSearchResult = baseSearchResultItem.extend({
  assetType: z.literal("folder"),
  isShared: z.boolean().optional(),
});

export const searchResultItem = z.discriminatedUnion("assetType", [
  zapSearchResult,
  nextGenZapSearchResult,
  tableSearchResult,
  interfaceSearchResult,
  canvasSearchResult,
  agentSearchResult,
  chatbotSearchResult,
  folderSearchResult,
]);

export type SearchResultItem = z.infer<typeof searchResultItem>;

export const globalSearchOutput = z.object({
  results: z.array(searchResultItem),
  resultsByType: z.object({
    zap: z.array(zapSearchResult),
    // Key order is the dropdown's section order: `SearchResults` renders
    // `Object.entries(resultsByType)` and zod rebuilds in schema order.
    nextGenZap: z.array(nextGenZapSearchResult),
    table: z.array(tableSearchResult),
    interface: z.array(interfaceSearchResult),
    canvas: z.array(canvasSearchResult),
    agent: z.array(agentSearchResult),
    chatbot: z.array(chatbotSearchResult),
    folder: z.array(folderSearchResult),
  }),
  totalCount: z.number(),
  counts: z.object({
    zap: z.number(),
    nextGenZap: z.number(),
    table: z.number(),
    interface: z.number(),
    canvas: z.number(),
    agent: z.number(),
    chatbot: z.number(),
    folder: z.number(),
  }),
});

export type GlobalSearchOutput = z.infer<typeof globalSearchOutput>;
